import basic from '../../../../packages/builder-schema/fixtures/01-basic.flow.v2.json';
import conditional from '../../../../packages/builder-schema/fixtures/02-conditional.flow.v2.json';
import api from '../../../../packages/builder-schema/fixtures/03-api.flow.v2.json';
import humanInput from '../../../../packages/builder-schema/fixtures/04-human-input.flow.v2.json';
import parallel from '../../../../packages/builder-schema/fixtures/05-parallel.flow.v2.json';
import candidateScreening from '../../../../packages/builder-schema/fixtures/06-candidate-screening.flow.v2.json';
import interviewScorecard from '../../../../packages/builder-schema/fixtures/07-interview-scorecard.flow.v2.json';
import applicationTypeAdvisor from '../../../../packages/builder-schema/fixtures/08-application-type-advisor.flow.v2.json';

const clone = (x) => JSON.parse(JSON.stringify(x));

/** The shared fixture leaves the branch unconfigured; the server rejects a conditional without `config.condition`. */
function withConfiguredBranch(def) {
  const trueLabel = 'Has input';
  const falseLabel = 'No input';
  return {
    ...def,
    nodes: def.nodes.map((n) => (n.type === 'conditional' && !n.config?.condition
      ? {
        ...n,
        label: n.label || 'Check the input',
        config: {
          ...(n.config || {}),
          condition: "String(state?.input?.message ?? state?.input ?? '').trim().length > 0",
          trueLabel,
          falseLabel,
        },
      }
      : n)),
    edges: def.edges.map((e) => {
      if (e.source !== 'condition' || e.label) return e;
      return { ...e, label: e.target === 'end_yes' ? trueLabel : falseLabel };
    }),
  };
}

export const STUDIO_V2_FLOW_TEMPLATES = [
  {
    id: 'basic',
    label: 'Start → End',
    description: 'Minimal linear path — ideal first graph.',
    category: 'foundations',
    tags: ['beginner', 'linear'],
    definition: clone(basic),
  },
  {
    id: 'conditional',
    label: 'Branching',
    description: 'Route by condition with success / failure edges.',
    category: 'foundations',
    tags: ['branch', 'logic'],
    definition: withConfiguredBranch(clone(conditional)),
  },
  {
    id: 'api',
    label: 'API call',
    description: 'Call an HTTP endpoint and wire outputs forward.',
    category: 'foundations',
    tags: ['integration', 'http'],
    definition: clone(api),
  },
  {
    id: 'human-input',
    label: 'Human input',
    description: 'Pause for approval or typed input before continuing.',
    category: 'patterns',
    tags: ['hitl', 'approval'],
    definition: clone(humanInput),
  },
  {
    id: 'parallel',
    label: 'Parallel / Join',
    description: 'Fork work and merge results.',
    category: 'patterns',
    tags: ['concurrency', 'join'],
    definition: clone(parallel),
  },
  {
    id: 'candidate-screening',
    label: 'Candidate screening',
    description: 'Intake gate → conditional LLM rubric → result.screening for queue + detail panels.',
    category: 'patterns',
    tags: ['recruiting', 'screening', 'rubric', 'talent'],
    definition: clone(candidateScreening),
  },
  {
    id: 'interview-scorecard',
    label: 'Interview scorecard',
    description: 'Normalize feedback → LLM summary → result.summary + result.scorecard for manager console.',
    category: 'patterns',
    tags: ['recruiting', 'scorecard', 'summary', 'talent'],
    definition: clone(interviewScorecard),
  },
  {
    id: 'application-type-advisor',
    label: 'SF application-type advisor',
    description: 'CLAW skill if-sf-application-advisor (agent tool loop) → result.routing for San Francisco.',
    category: 'patterns',
    tags: ['permits', 'planning', 'license', 'claw', 'skills', 'government', 'san-francisco'],
    definition: clone(applicationTypeAdvisor),
  },
];

/**
 * Replace nodes/edges (and template name) while preserving schemaVersion, id, metadata keys except lastTemplateId.
 */
export function mergeTemplateIntoCurrentFlow(currentText, templateDef, templateId) {
  let cur;
  try {
    cur = JSON.parse(currentText || '{}');
  } catch {
    return { error: 'Current flow JSON is invalid.' };
  }
  if (!cur || typeof cur !== 'object') return { error: 'Invalid flow object.' };
  const next = {
    ...cur,
    id: cur.id,
    name: templateDef.name || cur.name,
    description: templateDef.description ?? cur.description,
    nodes: Array.isArray(templateDef.nodes) ? templateDef.nodes : cur.nodes,
    edges: Array.isArray(templateDef.edges) ? templateDef.edges : cur.edges,
    metadata: {
      ...(cur.metadata && typeof cur.metadata === 'object' ? cur.metadata : {}),
      lastTemplateId: templateId,
      lastTemplateAt: new Date().toISOString(),
    },
  };
  return { text: JSON.stringify(next, null, 2) };
}
